import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AlertTriangle, LogOut, ShieldAlert,
  ChevronRight, Loader2, Download, Lock, Check,
} from "lucide-react";
import TimelineView from "@/components/timeline/TimelineView";
import { fileUrl } from "@/lib/uploads";

const currency = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

function TabButton({ active, testid, onClick, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-[10px] font-mono uppercase tracking-[0.25em] border-b-2 whitespace-nowrap transition-all ${
        active ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-gray-500 hover:text-gray-300"
      }`}
    >
      {children}
    </button>
  );
}

function PhasesTimeline({ phases = [] }) {
  return (
    <section
      className="border border-[#262626] rounded-md bg-[#0a0a0a] p-5"
      data-testid="phases-timeline"
    >
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-4">Project Phases</p>
      <ol className="space-y-3">
        {phases.map((p, i) => {
          const state = p.completed ? "completed" : p.unlocked ? "in_progress" : "locked";
          const dot = state === "completed"
            ? "bg-[#d4af37] text-black"
            : state === "in_progress"
              ? "border border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10"
              : "border border-[#262626] text-gray-600";
          const labelCls = state === "locked" ? "text-gray-600" : "text-white";
          return (
            <li key={p.key} className="flex items-center gap-4" data-testid={`phase-${p.key}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${dot}`}>
                {state === "completed" ? (
                  <Check className="w-4 h-4" strokeWidth={2.5} />
                ) : state === "locked" ? (
                  <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />
                ) : (
                  <span className="text-[10px] font-mono">{i + 1}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${labelCls}`}>{p.label}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                  {state === "completed" ? "Completed"
                    : state === "in_progress" ? "In progress"
                      : "Locked · unlocks with progress"}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function DesignApprovalPanel({ design, onApprove, onRemark }) {
  const [openIdx, setOpenIdx] = React.useState(null);
  const [remark, setRemark] = React.useState("");
  const variations = design?.variations || [];
  const approved = !!design?.client_approved_at;
  return (
    <section className="border border-[#262626] rounded-md bg-[#0a0a0a] p-5" data-testid="design-approval-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Design & Approvals</p>
        {approved ? (
          <span className="text-[11px] font-mono text-[#10b981]">✓ Approved · {new Date(design.client_approved_at).toLocaleDateString()}</span>
        ) : design?.can_approve && (
          <button onClick={onApprove} className="iz-btn-primary text-xs" data-testid="approve-design-btn">Approve Design</button>
        )}
      </div>
      {variations.length === 0 && <p className="text-xs text-gray-500 italic py-4">Designer hasn&apos;t shared any variations yet.</p>}
      <ul className="space-y-2">
        {variations.slice().reverse().map((v, i) => {
          const realIdx = variations.length - 1 - i;
          return (
            <li key={realIdx} className="border border-[#1a1a1a] rounded-md p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm text-white">Variation #{v.iteration_no || realIdx + 1}</p>
                  <p className="text-[10px] font-mono text-gray-500">{v.uploaded_at ? new Date(v.uploaded_at).toLocaleDateString() : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  {v.data_url && <a href={v.data_url} download={v.name || "variation"} target="_blank" rel="noreferrer" className="iz-btn-secondary text-xs">Download</a>}
                  <button onClick={() => { setOpenIdx(realIdx); setRemark(""); }} className="iz-btn-secondary text-xs" data-testid={`remark-btn-${realIdx}`}>Remark</button>
                </div>
              </div>
              {openIdx === realIdx && (
                <div className="mt-3 space-y-2">
                  <textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)}
                    className="iz-input" placeholder="Your remark to the designer…"
                    data-testid={`remark-input-${realIdx}`} />
                  <div className="flex justify-end gap-2">
                    <button className="iz-btn-secondary text-[10px]" onClick={() => setOpenIdx(null)}>Cancel</button>
                    <button onClick={() => { onRemark(realIdx, remark); setOpenIdx(null); }}
                      disabled={remark.trim().length < 2}
                      className="iz-btn-primary text-[10px] disabled:opacity-50">Send</button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {design?.file_remarks?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-[#1a1a1a]">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">My remarks history</p>
          <ul className="space-y-1.5 text-xs text-gray-400">
            {design.file_remarks.slice().reverse().slice(0, 6).map((r, i) => (
              <li key={i}>
                <span className="text-[#d4af37] font-mono text-[10px]">#{(r.variation_index ?? -1) + 1}</span> · &ldquo;{r.remark}&rdquo;
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** All designer-uploaded project files, exposed to the client with download links.
 * Auto-syncs on the 20s dashboard poll — new uploads appear without a page refresh. */
function DesignFilesPanel({ files }) {
  if (!files) return null;
  const drafts = files.design_draft || [];
  const finals = files.final_execution_set || [];
  const momBatches = files.offline_mom || [];
  const signoff = files.signoff_sheet;
  const totalCount = drafts.length + finals.length
    + momBatches.reduce((n, b) => n + (b.files?.length || 0), 0)
    + (signoff ? 1 : 0);

  if (totalCount === 0) return null;

  return (
    <section className="border border-[#262626] rounded-md bg-[#0a0a0a] p-5" data-testid="design-files-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Project Files</p>
          <p className="text-xs text-gray-500 font-mono">All design deliverables · auto-syncs every 20s</p>
        </div>
        <span className="text-[10px] font-mono text-[#d4af37] bg-[#d4af37]/10 px-2 py-1 rounded" data-testid="files-total-count">
          {totalCount} file{totalCount === 1 ? "" : "s"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FileCategoryCard testid="cat-design-draft" title="Design Drafts" note="Concept sheets from your Designer">
          <FileFlatList list={drafts} testid="draft" />
        </FileCategoryCard>

        <FileCategoryCard testid="cat-signoff" title="Sign-Off Sheet" note="Ready for your digital approval">
          {signoff ? (
            <FileFlatList list={[signoff]} testid="signoff" />
          ) : (
            <p className="text-[11px] text-gray-600 italic py-2">Not shared yet.</p>
          )}
        </FileCategoryCard>

        <FileCategoryCard testid="cat-mom" title="Offline MOM · Meeting Minutes" note="Uploaded per site-visit session">
          {momBatches.length === 0 ? (
            <p className="text-[11px] text-gray-600 italic py-2">No offline sessions yet.</p>
          ) : (
            <div className="space-y-3">
              {momBatches.map((b, bi) => (
                <div key={b.batch_id || bi} className="pl-2 border-l border-[#1a1a1a]/60" data-testid={`mom-session-${bi}`}>
                  <p className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">
                    Session {bi + 1} · {b.uploaded_at ? new Date(b.uploaded_at).toLocaleString() : ""}
                    {b.by_user_name ? ` · ${b.by_user_name}` : ""}
                  </p>
                  <FileFlatList list={b.files || []} testid={`mom-${bi}`} />
                </div>
              ))}
            </div>
          )}
        </FileCategoryCard>

        <FileCategoryCard testid="cat-final" title="Final Execution Sheet" note="Production-ready drawings">
          <FileFlatList list={finals} testid="final" />
        </FileCategoryCard>
      </div>
    </section>
  );
}

function FileCategoryCard({ title, note, testid, children }) {
  return (
    <div className="border border-[#1a1a1a] rounded-md p-3 bg-[#050505]" data-testid={testid}>
      <p className="text-xs text-white">{title}</p>
      <p className="text-[10px] font-mono text-gray-500 mb-2">{note}</p>
      {children}
    </div>
  );
}

function FileFlatList({ list, testid }) {
  if (!list || list.length === 0) return <p className="text-[11px] text-gray-600 italic py-1">Not shared yet.</p>;
  return (
    <ul className="space-y-1">
      {list.map((f, i) => (
        <li key={f.id || i} className="flex items-center gap-2 text-xs" data-testid={`file-${testid}-${i}`}>
          <Download className="w-3.5 h-3.5 text-[#d4af37] shrink-0" strokeWidth={1.5} />
          <a href={f.data_url} download={f.name || "file"} target="_blank" rel="noreferrer"
             className="text-[#d4af37] hover:underline truncate flex-1"
             data-testid={`file-download-${testid}-${i}`}>
            {f.name || "file"}
          </a>
          <span className="text-[10px] font-mono text-gray-600 shrink-0">
            {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Chronological project activity feed — merges every event across all phases.
 * Rendered inside a dedicated "Activity" tab on the client dashboard. */
function ActivityFeed({ activity = [] }) {
  if (activity.length === 0) {
    return <p className="text-center py-16 text-gray-500 text-xs">No activity yet. Updates will stream here as your project progresses.</p>;
  }
  return (
    <div className="space-y-1" data-testid="activity-feed">
      {activity.map((e) => (
        <div key={e.id} className="flex gap-3 border-l-2 border-[#1a1a1a] pl-3 py-2 hover:border-[#d4af37]/40 transition-colors"
             data-testid={`activity-item-${e.kind}`}>
          <div className={`w-1.5 h-1.5 mt-2 rounded-full shrink-0 ${
            e.severity === "success" ? "bg-emerald-400"
              : e.severity === "warning" ? "bg-amber-400"
              : "bg-[#d4af37]/70"
          }`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white">{e.title}</p>
            {e.subtitle && <p className="text-xs text-gray-400 mt-0.5 whitespace-pre-line">{e.subtitle}</p>}
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
              {e.at ? new Date(e.at).toLocaleString() : ""}
              {e.actor ? ` · by ${e.actor}` : ""}
            </p>
            {e.files && e.files.length > 0 && (
              <div className="mt-2 space-y-1">
                {e.files.map((f, i) => (
                  <a key={i} href={f.data_url} download={f.name || "file"} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1.5 text-[11px] text-[#d4af37] hover:underline mr-3">
                    <Download className="w-3 h-3" strokeWidth={1.5} />
                    {f.name || "file"}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function StakeholdersConcernPanel({ stakeholders, onRaise }) {
  const [open, setOpen] = React.useState(false);
  const [subject, setSubject] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [targetId, setTargetId] = React.useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!targetId || subject.trim().length < 3 || description.trim().length < 5) return;
    onRaise({ subject, description, target_user_id: targetId });
    setOpen(false); setSubject(""); setDescription(""); setTargetId("");
  };

  return (
    <section className="border border-[#262626] rounded-md bg-[#0a0a0a] p-5" data-testid="stakeholders-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Stakeholders</p>
          <p className="text-xs text-gray-500 font-mono">Reach out to anyone involved in your project</p>
        </div>
        <button onClick={() => setOpen(true)} className="iz-btn-primary text-xs" data-testid="raise-concern-btn">Raise a Concern</button>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {stakeholders.map((s) => (
          <li key={s.id} className="border border-[#1a1a1a] rounded-md px-3 py-2 text-xs" data-testid={`stakeholder-${s.id}`}>
            <p className="text-white">{s.name}</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{s.role}</p>
          </li>
        ))}
      </ul>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="concern-modal">
          <div className="w-full max-w-lg iz-card p-6" onClick={(e) => e.stopPropagation()}>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-1">Concern</p>
            <h3 className="font-display text-xl text-white mb-4">Raise a concern</h3>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">To *</label>
                <select value={targetId} onChange={(e) => setTargetId(e.target.value)} className="iz-input" required data-testid="concern-target">
                  <option value="">— Pick a stakeholder —</option>
                  {stakeholders.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.role}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Subject *</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} className="iz-input" required minLength={3} data-testid="concern-subject"/>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Description *</label>
                <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} className="iz-input" required minLength={5} data-testid="concern-description"/>
              </div>
              <p className="text-[10px] text-gray-500">Admins, Directors, and the CRM Manager are automatically CC&apos;d.</p>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="iz-btn-secondary text-xs">Cancel</button>
                <button type="submit" className="iz-btn-primary text-xs" data-testid="concern-submit">Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}


function GateScreen({ project }) {
  return (
    <div className="max-w-2xl mx-auto py-16 px-6 text-center" data-testid="gate-screen">
      <div className="border border-[#d4af37]/30 rounded-md p-10 bg-[#0a0a0a] iz-grain">
        <div className="w-16 h-16 mx-auto mb-6 border border-[#d4af37] rounded-full flex items-center justify-center">
          <Lock className="w-6 h-6 text-[#d4af37]" strokeWidth={1.5} />
        </div>
        <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#d4af37] mb-2">Payment milestone pending</p>
        <h2 className="font-display text-3xl text-white mb-4">Your workspace is being prepared</h2>
        <p className="text-sm text-gray-400 max-w-md mx-auto">
          The full tracking dashboard, live site diary, and 13-point timeline will unlock the moment
          our Finance team confirms your <span className="text-[#d4af37]">45% milestone payment</span>.
        </p>
        {project?.name && (
          <p className="text-xs text-gray-500 mt-6 font-mono">
            Project · {project.name}
          </p>
        )}
        <p className="text-[11px] text-gray-500 mt-8">
          Received: <span className="text-white font-mono">{currency(project?.amount_received)}</span> · Contract:{" "}
          <span className="text-white font-mono">{currency(project?.project_amount)}</span>
        </p>
      </div>
    </div>
  );
}

function AlertBanner({ alert, onDismiss }) {
  const severity = alert.severity;
  const tone = severity === "high"
    ? "border-[#d4af37]/60 bg-gradient-to-r from-[#d4af37]/10 to-transparent"
    : "border-emerald-500/40 bg-emerald-500/5";
  return (
    <div
      className={`border-l-4 ${tone} border border-[#262626] rounded-md p-4 flex items-start gap-3`}
      data-testid={`alert-${alert.id}`}
    >
      <ShieldAlert className={`w-5 h-5 mt-0.5 ${severity === "high" ? "text-[#d4af37]" : "text-emerald-300"}`} strokeWidth={1.5} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${severity === "high" ? "text-[#d4af37]" : "text-emerald-200"} font-semibold`}>{alert.title}</p>
        <p className="text-xs text-gray-300 mt-1">{alert.body}</p>
        {alert.amount_due != null && alert.amount_due > 0 && (
          <p className="text-[11px] font-mono text-gray-400 mt-2">
            Amount due · <span className="text-[#d4af37]">{currency(alert.amount_due)}</span>
          </p>
        )}
      </div>
    </div>
  );
}

function SnagRadialGauge({ summary }) {
  const pct = summary?.completion_percent ?? 0;
  const size = 160;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div className="flex items-center gap-6" data-testid="snag-gauge">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} stroke="#1c1c1c" fill="none" />
          <circle
            cx={size / 2} cy={size / 2} r={r}
            strokeWidth={stroke} stroke="#d4af37" fill="none"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circ}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: "stroke-dasharray 0.6s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-3xl text-[#d4af37]">{pct}%</span>
          <span className="text-[9px] font-mono uppercase tracking-widest text-gray-500 mt-1">verified</span>
        </div>
      </div>
      <div className="text-xs space-y-1">
        <p className="font-mono text-emerald-300">✓ verified: {summary?.verified || 0}</p>
        <p className="font-mono text-[#d4af37]">◐ resolved: {summary?.resolved || 0}</p>
        <p className="font-mono text-gray-400">○ open: {summary?.open || 0}</p>
        <p className="font-mono text-gray-500 pt-1">total logged: {summary?.total || 0}</p>
      </div>
    </div>
  );
}

function ExecutionStrip({ execution }) {
  if (!execution.stages || execution.stages.length === 0) return null;
  return (
    <div className="border border-[#262626] rounded-md bg-[#0a0a0a] p-5" data-testid="execution-strip">
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">Site Execution · 8-Stage Progress</p>
      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {execution.stages.map((s, i) => {
          const isActive = s.key === execution.current_stage_key;
          const done = s.status === "completed";
          return (
            <React.Fragment key={s.key}>
              <div
                className={`flex-1 min-w-[110px] rounded p-3 border transition-all ${
                  isActive
                    ? "border-[#d4af37] bg-[#d4af37]/10 shadow-[0_0_16px_rgba(212,175,55,0.15)]"
                    : done
                    ? "border-emerald-500/30 bg-emerald-500/5"
                    : "border-[#1c1c1c] bg-[#0f0f0f]"
                }`}
                data-testid={`exec-stage-${s.key}`}
              >
                <p className={`text-[9px] font-mono uppercase tracking-widest ${
                  isActive ? "text-[#d4af37]" : done ? "text-emerald-300" : "text-gray-500"
                }`}>
                  {String(i + 1).padStart(2, "0")} · {s.status}
                </p>
                <p className={`text-xs mt-1 ${isActive ? "text-white" : "text-gray-300"}`}>{s.label}</p>
                {s.completed_at && (
                  <p className="text-[9px] font-mono text-gray-500 mt-1">
                    {new Date(s.completed_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              {i < execution.stages.length - 1 && (
                <ChevronRight className={`w-3 h-3 flex-shrink-0 ${done ? "text-emerald-500" : "text-gray-700"}`} strokeWidth={1.5} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function SiteDiary({ projectId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client/site-diary");
      setEntries(data.entries || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  if (loading) return <p className="text-xs text-gray-500 py-8 text-center">Loading…</p>;
  if (entries.length === 0) return (
    <div className="text-center py-16 text-gray-500 text-xs" data-testid="diary-empty">
      Your site supervisor hasn&apos;t posted any updates yet. Check back soon.
    </div>
  );

  return (
    <div className="space-y-4" data-testid="site-diary">
      {entries.map((e) => (
        <div key={e.id} className="border border-[#262626] rounded-md p-4 bg-[#0f0f0f]">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${
              e.kind === "site_log"
                ? "text-[#d4af37] border-[#d4af37]/40"
                : "text-gray-300 border-gray-600/40"
            }`}>
              {e.kind === "site_log" ? "Site Update" : "Factory"}
            </span>
            <span className="text-[10px] font-mono text-gray-500">
              {e.at ? new Date(e.at).toLocaleString() : "—"}
            </span>
          </div>
          <p className="text-sm text-white">{e.title}</p>
          {e.body && <p className="text-xs text-gray-400 mt-1">{e.body}</p>}
          {e.photos && e.photos.length > 0 && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              {e.photos.slice(0, 4).map((fid) => (
                <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer">
                  <img
                    src={fileUrl(fid)}
                    alt=""
                    className="w-full h-24 object-cover rounded border border-[#262626] hover:border-[#d4af37]/40 transition-all"
                    onError={(ev) => { ev.currentTarget.style.display = "none"; }}
                  />
                </a>
              ))}
            </div>
          )}
          {e.by && <p className="text-[10px] font-mono text-gray-500 mt-2">— {e.by}</p>}
        </div>
      ))}
    </div>
  );
}

function ArchivePanel({ archive, publicToken }) {
  if (!archive || archive.length === 0) return (
    <div className="text-center py-16 text-gray-500 text-xs">
      Your project archive will populate as milestones close.
    </div>
  );
  return (
    <div className="space-y-3" data-testid="archive-panel">
      {archive.map((entry) => {
        const isHandover = entry.kind === "handover_package";
        const downloadUrl = isHandover && publicToken
          ? `${process.env.REACT_APP_BACKEND_URL}/api/public/projects/${publicToken}/handover-package.zip`
          : null;
        return (
          <div key={entry.id} className="border border-[#262626] rounded-md p-4 bg-[#0f0f0f]" data-testid={`archive-${entry.kind}`}>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">
                  {entry.kind.replace(/_/g, " ")}
                </p>
                <p className="text-sm text-white mt-1">{entry.title}</p>
                <p className="text-[10px] font-mono text-gray-500 mt-1">
                  {new Date(entry.created_at).toLocaleString()}
                  {entry.balance_due != null && ` · balance ${currency(entry.balance_due)}`}
                </p>
              </div>
              {downloadUrl && (
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="iz-btn-primary text-xs flex items-center gap-2"
                  data-testid="archive-download"
                >
                  <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> Download
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ClientDashboardPage() {
  const { principal, logout } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("timeline");
  const [showRaise, setShowRaise] = useState(false);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [tagAll, setTagAll] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [concerns, setConcerns] = useState([]);

  const load = useCallback(async () => {
    try {
      const [dRes, cRes] = await Promise.all([
        api.get("/client/dashboard"),
        api.get("/crm/concerns").catch(() => ({ data: [] })),
      ]);
      setData(dRes.data);
      setConcerns(cRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, [load]);

  const submitConcern = async (e) => {
    e.preventDefault();
    if (!subject || !description) { setErr("Subject and description required"); return; }
    setSaving(true);
    try {
      await api.post("/crm/concerns", { subject, description, tag_all: tagAll, tagged_user_ids: [] });
      toast.success("Concern raised. CRM notified.");
      setSubject(""); setDescription(""); setShowRaise(false); load();
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const gateUnlocked = data?.gate?.is_45_percent_paid;

  // Force password reset on first-time (default Client@123) login
  if (principal?.must_reset_password) {
    return <Navigate to="/client/set-password" replace />;
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-[#262626] px-4 sm:px-10 py-4 sm:py-5 flex items-center justify-between bg-[#050505]/90 backdrop-blur-xl sticky top-0 z-30">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/branding/interazzo-logo.png" alt="Interazzo" className="w-28 sm:w-40 h-auto object-contain flex-shrink-0" data-testid="brand-logo" />
          <div className="min-w-0">
            <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-[#d4af37] truncate">Client Portal</p>
            <h1 className="font-display text-base sm:text-xl text-white truncate">Welcome, {principal?.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowRaise(true)}
                  className="iz-btn-primary flex items-center gap-1.5 text-xs px-3 py-1.5"
                  data-testid="raise-concern">
            <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} /> <span className="hidden sm:inline">Raise Concern</span>
          </button>
          <button onClick={logout} className="iz-btn-secondary text-xs px-2 py-1.5" data-testid="client-logout">
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <main className="px-4 sm:px-10 py-6 sm:py-8 space-y-6 max-w-6xl mx-auto">
        {!data && <p className="text-center text-xs text-gray-500 py-16">Loading your workspace…</p>}

        {data?.phases && <PhasesTimeline phases={data.phases} />}
        {data?.design && (
          <DesignApprovalPanel
            design={data.design}
            onApprove={async () => {
              try {
                await api.post("/client/design/approve");
                toast.success("Design approved");
                load();
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
            }}
            onRemark={async (variationIndex, remark) => {
              try {
                await api.post("/client/design/file-remark",
                  { variation_index: variationIndex, remark });
                toast.success("Remark sent");
                load();
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
            }}
          />
        )}
        {data?.design?.files && <DesignFilesPanel files={data.design.files} />}
        {data?.stakeholders?.length > 0 && (
          <StakeholdersConcernPanel
            stakeholders={data.stakeholders}
            onRaise={async ({ subject, description, target_user_id }) => {
              try {
                await api.post("/crm/concerns", { subject, description, target_user_id });
                toast.success("Concern raised — the stakeholder + directors have been notified");
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
            }}
          />
        )}
        {data?.project?.final_amount != null && (
          <div className="border border-[#262626] rounded-md bg-[#0a0a0a] p-5" data-testid="project-value-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Project value</p>
                <p className="font-display text-2xl text-white">₹{Number(data.project.final_amount || 0).toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Remaining balance</p>
                <p className="text-sm text-[#d4af37]">₹{Number(data.project.remaining_balance || 0).toLocaleString()}</p>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  await api.post("/client/payment/request-ack");
                  toast.success("Ack request sent to Ops + Finance");
                } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
              }}
              className="iz-btn-secondary text-xs mt-4"
              data-testid="request-ack-btn"
            >
              Request Payment Acknowledgement
            </button>
          </div>
        )}

        {data && !gateUnlocked && (
          <>
            {data.activity && data.activity.length > 0 && (
              <section className="border border-[#262626] rounded-md bg-[#0a0a0a] p-5" data-testid="activity-preview-pregate">
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">Recent Activity</p>
                <ActivityFeed activity={(data.activity || []).slice(0, 8)} />
              </section>
            )}
            <GateScreen project={data.project} />
          </>
        )}

        {data && gateUnlocked && (
          <>
            {/* Alerts */}
            {data.alerts && data.alerts.length > 0 && (
              <div className="space-y-2" data-testid="alerts-section">
                {data.alerts.map((a) => <AlertBanner key={a.id} alert={a} />)}
              </div>
            )}

            {/* Project header card */}
            <section className="border border-[#262626] rounded-md bg-[#0a0a0a] p-5 flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
                  {data.execution?.status?.replace(/_/g, " ") || "in progress"}
                </p>
                <h2 className="font-display text-2xl text-white mt-1">{data.project?.name}</h2>
                <p className="text-xs text-gray-500 mt-1">
                  {data.project?.city && `${data.project.city} · `}
                  Contract <span className="text-white font-mono">{currency(data.project?.project_amount)}</span>
                  {" · "}Received <span className="text-white font-mono">{currency(data.project?.amount_received)}</span>
                </p>
              </div>
              {data.snag_summary?.total > 0 && (
                <div className="w-full sm:w-auto"><SnagRadialGauge summary={data.snag_summary} /></div>
              )}
            </section>

            {/* Execution strip */}
            {data.execution?.stages?.length > 0 && <ExecutionStrip execution={data.execution} />}

            {/* Tabs */}
            <div className="border-b border-[#262626] overflow-x-auto">
              <div className="flex min-w-max">
                <TabButton testid="tab-timeline" active={tab === "timeline"} onClick={() => setTab("timeline")}>
                  Timeline
                </TabButton>
                <TabButton testid="tab-activity" active={tab === "activity"} onClick={() => setTab("activity")}>
                  Activity ({data.activity?.length || 0})
                </TabButton>
                <TabButton testid="tab-diary" active={tab === "diary"} onClick={() => setTab("diary")}>
                  Site Diary
                </TabButton>
                <TabButton testid="tab-archive" active={tab === "archive"} onClick={() => setTab("archive")}>
                  Archive ({data.archive?.length || 0})
                </TabButton>
                <TabButton testid="tab-concerns" active={tab === "concerns"} onClick={() => setTab("concerns")}>
                  Concerns ({concerns.length})
                </TabButton>
              </div>
            </div>

            <section className="pt-2">
              {tab === "timeline" && (data.timeline ? <TimelineView timeline={data.timeline} /> :
                <p className="text-center py-16 text-gray-500 text-xs">Timeline computation in progress.</p>)}
              {tab === "activity" && <ActivityFeed activity={data.activity || []} />}
              {tab === "diary" && <SiteDiary projectId={data.project?.id} />}
              {tab === "archive" && <ArchivePanel archive={data.archive} publicToken={data.public_snag_token} />}
              {tab === "concerns" && (
                <div className="space-y-2">
                  {concerns.length === 0 && <p className="text-center py-16 text-gray-500 text-xs">No concerns raised. All calm.</p>}
                  {concerns.map((c) => (
                    <div key={c.id} className="border border-[#262626] rounded-md p-4 bg-[#0f0f0f]" data-testid={`client-concern-${c.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-white">{c.subject}</p>
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${c.status === "resolved" ? "border-emerald-500/40 text-emerald-400" : "border-[#d4af37]/40 text-[#d4af37]"}`}>
                          {c.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1 whitespace-pre-wrap">{c.description}</p>
                      {(c.replies || []).length > 0 && (
                        <div className="mt-3 pl-3 border-l-2 border-[#d4af37]/40 space-y-2" data-testid={`concern-replies-${c.id}`}>
                          <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#d4af37]">CRM Replies · {c.replies.length}</p>
                          {c.replies.map((r) => (
                            <div key={r.id} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-md p-3">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-[11px] font-mono text-gray-300">
                                  {r.by_user_name} <span className="text-gray-600">· {r.by_role}</span>
                                </p>
                                <p className="text-[10px] font-mono text-gray-600">{new Date(r.at).toLocaleString()}</p>
                              </div>
                              <p className="text-xs text-white whitespace-pre-wrap">{r.message}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      {showRaise && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShowRaise(false)}>
          <form onSubmit={submitConcern} className="border border-[#262626] rounded-md bg-[#0a0a0a] p-6 max-w-md w-full"
                onClick={(e) => e.stopPropagation()} data-testid="concern-modal">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-[#d4af37]/10 border border-[#d4af37] flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Priority Alert</p>
                <h2 className="font-display text-xl text-white">Raise a concern</h2>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Subject</label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} className="iz-input w-full" data-testid="concern-subject" required />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Description</label>
                <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} className="iz-input w-full" data-testid="concern-description" required />
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-300">
                <input type="checkbox" checked={tagAll} onChange={(e) => setTagAll(e.target.checked)} data-testid="tag-all" />
                Tag all project stakeholders (hyper-alert)
              </label>
              {err && <div className="text-xs text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowRaise(false)} className="iz-btn-secondary text-xs">Cancel</button>
                <button type="submit" disabled={saving} className="iz-btn-primary text-xs" data-testid="submit-concern">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Fire Alert"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
